import { DateTime } from 'luxon';

export const ROLE_TEACHER = 'teacher';
export const ROLE_DISTRICT_ADMIN = 'district_admin';
export const ROLE_STAFF = 'staff';
export const ROLE_STUDENT = 'student';

export const CLASSROOM_ROLE_GROUP = [ROLE_TEACHER];

export const DISTRICT_ROLE_GROUP = [ROLE_DISTRICT_ADMIN];

export const SCHOOL_ROLE_GROUP = [ROLE_STAFF];

export const DASHBOARD_ROLE_GROUP = [ROLE_TEACHER, ROLE_DISTRICT_ADMIN, ROLE_STAFF];

export const GOOGLE_PROVIDER_ID = 'google.com';

export const COPPA_AGE = 13;
export const ADULT_AGE = 19;
export const MAX_AGE = 122;

export const CLEVER_OIDC_PROVIDER_ID = process.env.GATSBY_YLJ_CLEVER_OIDC_PROVIDER_ID;
export const PASSWORD_PROVIDER_ID = 'password';

export const DEFAULT_USERNAME = 'Me';

/**
 * Has role
 *
 * @param {Object} user
 * @param {Array} roles
 * @return {boolean}
 */
export function hasRole(user, roles) {
  if (user && user.roles) {
    const matches = user.roles.find((role) => {
      return roles.includes(role);
    });

    return !!matches;
  }

  return false;
}

/**
 * Is district role group
 * Will this user be shown district-level insights?
 *
 * @param {string} role
 * @return {boolean}
 */
export function isDistrictRoleGroup(role) {
  return DISTRICT_ROLE_GROUP.indexOf(role) >= 0;
}

/**
 * Is school role group
 * Will this user be shown school-level insights?
 *
 * @param {string} role
 * @return {boolean}
 */
export function isSchoolRoleGroup(role) {
  return SCHOOL_ROLE_GROUP.indexOf(role) >= 0;
}

/**
 * Is classroom role group
 * Will this user be shown classroom-level insights?
 *
 * @param {string} role
 * @return {boolean}
 */
export function isClassroomRoleGroup(role) {
  return CLASSROOM_ROLE_GROUP.indexOf(role) >= 0;
}

/*
 * Birthday to age
 *
 * @param {Date|String} birthdayString
 * @return {Number}
 */
export function birthdayToAge(input) {
  const now = DateTime.now().startOf('day');
  let birthday;
  let birthdayTime;

  if (typeof input === 'string') {
    const birthdaySplit = input.split('-').map((v) => parseInt(v, 10));
    birthday = DateTime.local(birthdaySplit[0], birthdaySplit[1], birthdaySplit[2], 0, 0);
    birthdayTime = new Date(input);
  } else if (input instanceof Date) {
    birthday = DateTime.fromSeconds(input.getTime() / 1000);
    birthdayTime = input;
  } else {
    return 0;
  }

  const targetCoppa = now.set({ year: now.year - COPPA_AGE });
  const targetAdult = now.set({ year: now.year - ADULT_AGE });
  const likelyAge = Math.floor((new Date() - birthdayTime.getTime()) / 3.15576e10);

  if (birthday.invalid) {
    return 0;
  }

  if (birthday.year < now.year - MAX_AGE) {
    return 0;
  }

  if (birthday.ts <= targetAdult.ts) {
    return Math.max(ADULT_AGE, likelyAge);
  }

  if (birthday.ts <= targetCoppa.ts) {
    return Math.max(COPPA_AGE, likelyAge);
  }

  return Math.min(COPPA_AGE - 1, likelyAge);
}

/**
 * Add age related fields
 *
 * @param {Object} data
 * @param {number} age
 * @param {DateTime} birthday
 * @return {Object}
 */
export function addAgeRelatedFields(data, age, birthday) {
  data.coppaAge = age >= COPPA_AGE;
  data.adultAge = age >= ADULT_AGE;

  if (age >= COPPA_AGE) {
    data.birthday = birthday;
  }

  return data;
}

export function canUserJoinClass(user) {
  return user && hasRole(user, [ROLE_STUDENT]);
}

export function parseGoogleUsername(user) {
  let username;

  try {
    username = user.displayName.trim() || user.email.replace(/@.+/, '').trim() || 'User';
  } catch (error) {
    username = 'User';
  }

  return username;
}

export function parseCleverUsername(tokenResult) {
  let username;

  try {
    const name = JSON.parse(tokenResult.claims.name);
    username = `${name.first.trim()} ${name.last.trim()}`;
  } catch (error) {
    username = 'User';
  }

  return username;
}
